import { DEG_TO_RAD, gamePadLandScapeResourceNames, gamePadLandScapeResources } from '../../../core/gameConstants';
import { gamePadLocaleState } from '../state';
import { AUTO_SPIN_COUNTS, AUTO_SPIN_INDEX, SPIN_AMOUNT, SPIN_AMOUNT_ACTIVE } from '../../../stateManagment/gameState';
import { wheelDrawer } from '../../wheel/WheelDrawer';
import gsap, { Expo } from 'gsap';
import { getTranslation, translationKeys } from '../../../core/translation/translation';
import { BaseGamePadDrawer } from './BaseGamePadDrawer';
import {gameDrawer} from "../../../game/GameDrawer";

class GamePadMobileLandScapeDrawer extends BaseGamePadDrawer {
  constructor() {
    super();
    this.container = this.createContainer();
    this.loader = this.createLoader();
    this.resources = null;
    this.sizes = {
      margin: 12,
      containerPosition: {
        x: 1330, y: 160,
      },
      circle: {
        w: 160,
        h: 160,
        d: 50,
      },
      spin: {
        w: 260,
        h: 260,
      },
    };
  }

  async loadResource() {
    if (!this.resources) {
      const { res } = await super.loadResource(gamePadLandScapeResources, this.loader);
      this.resources = res;
    }
    return this.resources;
  }

  setupContainer() {
    this.setPosition(this.container, this.sizes.containerPosition);
  }

  drawAutoSpin() {
    const { circle, spin } = this.sizes;
    const [autoSpin, autoSpinContainer, setAutoSpinActive] = this._createGamePadCircle(
      gamePadLandScapeResourceNames.autoplayIcon,
      gamePadLocaleState.autoSpinButtonActive.getValue(),
    );
    this.addSubscriber(gamePadLocaleState.autoSpinButtonActive.subscribe(bool => {
      setAutoSpinActive(bool);
    }));
    autoSpin.on('pointerdown', () => {
      const autoSpinButtonActive = gamePadLocaleState.autoSpinButtonActive.getValue();
      gamePadLocaleState.autoSpinButtonActive.next(!autoSpinButtonActive);
    });
    this.setPosition(autoSpinContainer, { x: (spin.w - circle.w) / 2, y: 0 });
    this.container.addChild(autoSpinContainer);
    this.__drawAutoSpinController(autoSpinContainer);
  }

  __drawAutoSpinController(root) {
    const s = 80;
    const { circle: { w }, margin } = this.sizes;
    const content = this.createSprite(this.resources[gamePadLandScapeResourceNames.mobileController]);
    const prevContent = new this.PIXI.Graphics()
      .beginFill(0xffaaaa)
      .drawRect(12, 52, s, s);
    const prevCircle = new this.PIXI.Graphics()
      .beginFill(0xff0000)
      .drawCircle(12 + s / 2, 52 + s / 2, s / 2);
    const next = this.createSprite(this.resources[gamePadLandScapeResourceNames.rightArrowIcon]);
    const prev = this.createSprite(this.resources[gamePadLandScapeResourceNames.leftArrowIcon]);
    this.setPosition(prev, { x: 12, y: 52 });
    this.setPosition(next, { x: 206, y: 52 });
    prev.interactive = true;
    prev.buttonMode = true;
    next.interactive = true;
    next.buttonMode = true;
    content.scale.set(1.1);
    prev.scale.set(.36);
    next.scale.set(.54);
    const container = this.createContainer();
    this.setPosition(container, { x: w + margin, y: 0 });
    const amount = AUTO_SPIN_COUNTS.getValue()[AUTO_SPIN_INDEX.getValue()];
    const amountText = new this.PIXI.Text(amount, {
      fill: 0xFFFFFF,
      fontSize: 34,
      align: 'center',
    });
    amountText.anchor.set(.5);
    this.setPosition(amountText, { x: 150, y: 90 });
    if (gamePadLocaleState.betButtonActive.getValue()) {
      container.addChild(content);
    }
    content.scale.set(1.1);
    this.addSubscriber(gamePadLocaleState.autoSpinButtonActive.subscribe(bool => {
      if (bool) {
        container.addChild(content);
      } else {
        container.children = [];
      }
    }));
    this.addSubscriber(AUTO_SPIN_INDEX.subscribe(index => {
      amountText.text = AUTO_SPIN_COUNTS.getValue()[index];
    }));
    next.on('pointerdown', () => {
      let index = AUTO_SPIN_INDEX.getValue();
      if (AUTO_SPIN_COUNTS.getValue().length - 1 > AUTO_SPIN_INDEX.getValue()) {
        AUTO_SPIN_INDEX.next(++index);
      }
    });
    prev.on('pointerdown', () => {
      let index = AUTO_SPIN_INDEX.getValue();
      if (0 < AUTO_SPIN_INDEX.getValue()) {
        AUTO_SPIN_INDEX.next(--index);
      }
    });
    content.addChild(next);
    content.addChild(amountText);
    prevCircle.addChild(prev);
    prevContent.addChild(prevCircle);
    content.addChild(prevContent);
    root.addChild(container);
  }

  drawSpin() {
    const { margin, spin: { h, w }, circle: { h: circleH } } = this.sizes;
    const container = this.createContainer();
    this.setPosition(container, {
      x: 0,
      y: circleH + margin,
    });
    const rect = new this.PIXI.Graphics()
      .drawRect(
        0,
        0,
        w,
        h,
      );

    const spin = this.createSprite(this.resources[gamePadLandScapeResourceNames.spinIcon]);
    this.setPosition(spin, { x: w / 2, y: h / 2 });
    spin.scale.set(1.8);
    spin.anchor.set(.5);
    spin.interactive = true;
    spin.buttonMode = true;
    spin.on('pointerdown', () => {
      wheelDrawer.rotate();
      gsap.to(
        spin,
        {
          duration: .5,
          ease: String(Expo.easeOut(.8)),
          rotation: 25 * DEG_TO_RAD,
          onComplete: () => {
            gsap.to(
              spin,
              {
                duration: .5,
                ease: String(Expo.easeOut(.8)),
                rotation: 0,
              });
          },
        });
    });
    container.addChild(rect);
    container.addChild(spin);
    this.container.addChild(container);
  }

  drawBetAmount() {
    const { circle, spin, margin } = this.sizes;
    const [betAmount, betAmountContainer, setBetAmountActive] = this._createGamePadCircle(
      gamePadLandScapeResourceNames.betAmountIcon,
      gamePadLocaleState.betButtonActive.getValue(),
    );
    this.addSubscriber(gamePadLocaleState.betButtonActive.subscribe(bool => {
      setBetAmountActive(bool);
    }));

    betAmount.on('pointerdown', () => {
      const autoSpinButtonActive = gamePadLocaleState.betButtonActive.getValue();
      gamePadLocaleState.betButtonActive.next(!autoSpinButtonActive);
    });

    this.setPosition(betAmountContainer, { x: (spin.w - circle.w) / 2, y: spin.h + circle.h + 2 * margin });
    this.__drawBetAmountController(betAmountContainer);
    this.container.addChild(betAmountContainer);
  }

  __drawBetAmountController(root) {
    const { circle: { w }, margin } = this.sizes;
    const container = this.createContainer();
    this.setPosition(container, { x: w + margin, y: 0 });

    const content = this.createSprite(this.resources[gamePadLandScapeResourceNames.mobileController]);
    content.scale.set(1.1);

    const contentText = new this.PIXI.Text(`${getTranslation(translationKeys.BET)}`, {
      fill: 0xFFFFFF,
      fontSize: 34,
      align: 'center',
      fontWeight: 600
    });
    contentText.anchor.set(.5);
    this.setPosition(contentText, {x: 150, y: 20});
    const plus = this.__drawBetAmountButton({
      src: gamePadLandScapeResourceNames.betAmountPlus,
      position: { x: 206, y: 52 },
    });
    const minus = this.__drawBetAmountButton({
      src: gamePadLandScapeResourceNames.betAmountMinus,
      position: { x: 12, y: 52 },
    });

    const activeIndex = SPIN_AMOUNT_ACTIVE.getValue();

    const amountValue = SPIN_AMOUNT.getValue()[activeIndex];
    const amountText = new this.PIXI.Text(amountValue, {
      fill: 0xFFFFFF,
      fontSize: 34,
      align: 'center',
    });
    amountText.anchor.set(.5);
    this.setPosition(amountText, { x: 150, y: 90 });

    if (gamePadLocaleState.betButtonActive.getValue()) {
      container.addChild(content);
    }

    this.__setBetButtonsInitialState({ plus, minus });
    this.__setupBetAmountUpdates({ plus, minus, amountText, content, container });

    content.addChild(plus);
    content.addChild(minus);
    content.addChild(amountText);
    content.addChild(contentText);
    root.addChild(container);
  }

  __drawBetAmountButton({ src, position }) {
    const button = this.createSprite(this.resources[src]);
    button.interactive = true;
    button.buttonMode = true;
    this.setPosition(button, position);
    button.scale.set(.54);
    return button;
  }

  __setupBetAmountUpdates({
    plus,
    minus,
    amountText,
    container,
    content,
  }) {
    this.addSubscriber(SPIN_AMOUNT_ACTIVE.subscribe(index => {
      index === 0 ? minus.alpha = .5 : minus.alpha = 1;
      index === SPIN_AMOUNT.getValue().length - 1 ? plus.alpha = .5 : plus.alpha = 1;
      amountText.text = SPIN_AMOUNT.getValue()[index];
    }));
    plus.on('pointerdown', () => {
      let index = SPIN_AMOUNT_ACTIVE.getValue();
      if (SPIN_AMOUNT.getValue().length - 1 > SPIN_AMOUNT_ACTIVE.getValue()) {
        SPIN_AMOUNT_ACTIVE.next(++index);
      }
    });
    minus.on('pointerdown', () => {
      let index = SPIN_AMOUNT_ACTIVE.getValue();
      if (0 < SPIN_AMOUNT_ACTIVE.getValue()) {
        SPIN_AMOUNT_ACTIVE.next(--index);
      }
    });
    this.addSubscriber(gamePadLocaleState.betButtonActive.subscribe(bool => {
      if (bool) {
        container.addChild(content);
      } else {
        container.children = [];
      }
    }));
  }

  __setBetButtonsInitialState({
    plus,
    minus,
  }) {
    const activeIndex = SPIN_AMOUNT_ACTIVE.getValue();
    activeIndex === 0 ? minus.alpha = .5 : minus.alpha = 1;
    activeIndex === SPIN_AMOUNT.getValue().length - 1 ? plus.alpha = .5 : plus.alpha = 1;
  }

  execute({ parent, config }) {
    this.loadResource().then(() => {
      this.container.children = [];
      this.setupContainer();
      this.drawAutoSpin();
      this.drawBetAmount();
      this.drawSpin();
      parent.stage.addChild(this.container);
    });

  }
}

export const gamePadMobileLandScapeDrawer = new GamePadMobileLandScapeDrawer();
